import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SortIcon from '@mui/icons-material/Sort';
import ViewAgendaIcon from '@mui/icons-material/ViewAgenda';
import ViewStreamIcon from '@mui/icons-material/ViewStream';

import { GroupingModeEnum } from '@e-socna/data-layer';
import { useIsPricingEditable } from '../../contexts/PricingEditionContext/hooks/useIsPricingEditable.hook';

export const SINGLETON_GROUP_LABEL = 'Groupe unique';

const formatGroupLabel = ({
  groupingMode,
  label,
  sectionLabel,
}: Readonly<{
  groupingMode?: GroupingModeEnum | null;
  label: string | null | undefined;
  sectionLabel?: string;
}>) => {
  if (groupingMode === GroupingModeEnum.Singleton) {
    return sectionLabel ?? SINGLETON_GROUP_LABEL;
  }

  return sectionLabel && label ? `${sectionLabel} · ${label}` : label;
};

interface Props {
  readonly editionMode: boolean;
  readonly groupingMode?: GroupingModeEnum | null;
  readonly isOptional?: boolean;
  readonly label: string | null | undefined;
  readonly onStartCreateGroup?: () => void;
  readonly onStartRemoveGroup?: () => void;
  readonly onStartRenameGroup?: () => void;
  readonly onStartReorder?: () => void;
  readonly onStartSwitchGroupingMode?: () => void;
  // The section the group belongs to: names a singleton group, prefixes the
  // label of a manual group.
  readonly sectionLabel?: string;
}

export const ServiceUsagesGroupBanner = ({
  editionMode,
  groupingMode,
  isOptional = false,
  label,
  onStartCreateGroup: handleStartCreateGroup,
  onStartRemoveGroup: handleStartRemoveGroup,
  onStartRenameGroup: handleStartRenameGroup,
  onStartReorder: handleStartReorder,
  onStartSwitchGroupingMode: handleStartSwitchGroupingMode,
  sectionLabel,
}: Readonly<Props>) => {
  const isPricingEditable = useIsPricingEditable();

  const isGrouped = groupingMode === GroupingModeEnum.Manual;

  const displayedLabel = formatGroupLabel({
    groupingMode,
    label,
    sectionLabel,
  });

  return (
    <Paper
      component="section"
      sx={{
        alignItems: 'center',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 1,
        justifyContent: 'space-between',
        mb: 2,
        px: 2,
        py: 1,
      }}
      variant="outlined"
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <Typography component="h2" variant="h6">
          {displayedLabel}
        </Typography>
        {isOptional ? (
          <Chip color="info" label="Option" size="small" variant="outlined" />
        ) : null}
      </Stack>
      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
        {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
        {!!editionMode && (
          <>
            {!!handleStartRenameGroup && (
              <Button
                disabled={!isPricingEditable}
                onClick={handleStartRenameGroup}
                size="small"
                startIcon={<EditIcon />}
              >
                Modifier
              </Button>
            )}
            {!!handleStartRemoveGroup && (
              <Button
                color="error"
                disabled={!isPricingEditable}
                onClick={handleStartRemoveGroup}
                size="small"
                startIcon={<DeleteIcon />}
              >
                Supprimer le groupe
              </Button>
            )}
          </>
        )}
        {!!handleStartCreateGroup && (
          <Button
            disabled={!isPricingEditable}
            onClick={handleStartCreateGroup}
            size="small"
            startIcon={<AddIcon />}
          >
            Créer un groupe
          </Button>
        )}
        {!!handleStartReorder && (
          <Button
            disabled={!isPricingEditable}
            onClick={handleStartReorder}
            size="small"
            startIcon={<SortIcon />}
          >
            Réordonner
          </Button>
        )}
        {!!handleStartSwitchGroupingMode && (
          <Button
            disabled={!isPricingEditable}
            onClick={handleStartSwitchGroupingMode}
            size="small"
            startIcon={isGrouped ? <ViewStreamIcon /> : <ViewAgendaIcon />}
          >
            {isGrouped ? 'Dégrouper' : 'Découper'}
          </Button>
        )}
      </Stack>
    </Paper>
  );
};
